import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  adminGrantBoost,
  adminListBoards,
  adminResetBoards,
  adminListListings,
  adminUpdateBoost,
  adminUpdateCategory,
} from "@/lib/admin.functions";
import { HEAT_AED_CENTS, formatHeat } from "@/lib/behot";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/boards")({
  head: () =>
    privatePageMetadata(
      "/nc/live/adm/boards",
      "Boards | Control room",
      "Internal administration.",
    ),
  component: AdminBoards,
});

function AdminBoards() {
  const qc = useQueryClient();
  const boards = useServerFn(adminListBoards);
  const listings = useServerFn(adminListListings);
  const grant = useServerFn(adminGrantBoost);
  const updateBoost = useServerFn(adminUpdateBoost);
  const updateCategory = useServerFn(adminUpdateCategory);
  const resetBoards = useServerFn(adminResetBoards);
  const [selected, setSelected] = useState<string>("");

  const { data, isLoading } = useQuery({
    queryKey: ["adm-boards"],
    queryFn: () => boards({ data: undefined }),
  });
  const { data: allListings = [] } = useQuery({
    queryKey: ["adm-boards-listings"],
    queryFn: () => listings({ data: { limit: 200 } }),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["adm-boards"] });

  const boostMut = useMutation({
    mutationFn: (input: {
      boostId: string;
      heat?: number;
      status?: "pending" | "active" | "expired" | "refunded" | "cancelled";
      extendDays?: number;
    }) => updateBoost({ data: input }),
    onSuccess: () => {
      toast.success("Boost updated.");
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed."),
  });

  const grantMut = useMutation({
    mutationFn: (input: { listingId: string; categoryId: string; heat: number; days: number }) =>
      grant({ data: input }),
    onSuccess: () => {
      toast.success("Heat granted.");
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Grant failed."),
  });

  const resetMut = useMutation({
    mutationFn: (input: { categoryId?: string; all: boolean; durationDays: number }) =>
      resetBoards({ data: input }),
    onSuccess: (r) => {
      toast.success(`Board reset — ${r.cleared} boost(s) cleared. New season started.`);
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Reset failed."),
  });

  const catMut = useMutation({
    mutationFn: (input: {
      categoryId: string;
      status?: string;
      stage1Enabled?: boolean;
      minimumBoostCents?: number;
      minimumIncrementCents?: number;
      boostDurationDays?: number;
      maxListingsVisible?: number;
      openingBoostCents?: number;
      openingBannerEnabled?: boolean;
      seasonEndsAt?: string;
    }) => updateCategory({ data: input }),
    onSuccess: () => {
      toast.success("Board settings saved.");
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed."),
  });

  if (isLoading || !data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const categories = data.categories;
  const activeCategory = selected || categories[0]?.id || "";
  const rows = data.boosts
    .filter((b) => b.category_id === activeCategory)
    .sort((a, b) => Number(b.value_cents) - Number(a.value_cents));
  const category = categories.find((c) => c.id === activeCategory);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {categories.map((c) => (
          <button
            key={c.id}
            onClick={() => setSelected(c.id)}
            className={`rounded-full border px-3 py-1 text-xs font-medium ${
              activeCategory === c.id ? "bg-primary text-primary-foreground" : "hover:bg-muted"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {category && (
        <div className="card-surface grid gap-3 p-4 md:grid-cols-4">
          <div className="space-y-1">
            <Label>Minimum boost (Heat)</Label>
            <Input
              type="number"
              defaultValue={Math.round(category.minimum_boost_cents / HEAT_AED_CENTS)}
              onBlur={(e) =>
                catMut.mutate({
                  categoryId: category.id,
                  minimumBoostCents: Number(e.target.value) * HEAT_AED_CENTS,
                })
              }
            />
          </div>
          <div className="space-y-1">
            <Label>Minimum increment (Heat)</Label>
            <Input
              type="number"
              defaultValue={Math.round(category.minimum_increment_cents / HEAT_AED_CENTS)}
              onBlur={(e) =>
                catMut.mutate({
                  categoryId: category.id,
                  minimumIncrementCents: Number(e.target.value) * HEAT_AED_CENTS,
                })
              }
            />
          </div>
          <div className="space-y-1">
            <Label>Board duration (days)</Label>
            <Input
              type="number"
              defaultValue={category.boost_duration_days}
              onBlur={(e) =>
                catMut.mutate({
                  categoryId: category.id,
                  boostDurationDays: Number(e.target.value),
                })
              }
            />
          </div>
          <div className="space-y-1">
            <Label>Slots visible</Label>
            <Input
              type="number"
              defaultValue={category.max_listings_visible}
              onBlur={(e) =>
                catMut.mutate({
                  categoryId: category.id,
                  maxListingsVisible: Number(e.target.value),
                })
              }
            />
          </div>
          <div className="space-y-1">
            <Label>Opening price when board is empty (Heat)</Label>
            <Input
              type="number"
              defaultValue={Math.round(category.opening_boost_cents / HEAT_AED_CENTS)}
              onBlur={(e) =>
                catMut.mutate({
                  categoryId: category.id,
                  openingBoostCents: Number(e.target.value) * HEAT_AED_CENTS,
                })
              }
            />
          </div>
          <div className="space-y-1">
            <Label>Season ends</Label>
            <Input
              type="date"
              defaultValue={
                category.season_ends_at
                  ? new Date(category.season_ends_at).toISOString().slice(0, 10)
                  : ""
              }
              onBlur={(e) =>
                catMut.mutate({ categoryId: category.id, seasonEndsAt: e.target.value })
              }
            />
          </div>
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <Switch
              checked={Boolean(category.opening_banner_enabled)}
              onCheckedChange={(v) =>
                catMut.mutate({ categoryId: category.id, openingBannerEnabled: v })
              }
            />
            Show the green "position 01 is open" banner while empty
          </label>
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <Switch
              checked={Boolean(category.stage1_enabled)}
              onCheckedChange={(v) => catMut.mutate({ categoryId: category.id, stage1Enabled: v })}
            />
            Board live (stage 1 enabled)
          </label>
        </div>
      )}

      {category && (
        <div className="card-surface flex flex-wrap items-center gap-3 border-emerald-500/40 p-4">
          <div className="min-w-0 flex-1">
            <p className="font-display text-sm font-bold">
              Season {category.season_number} · started{" "}
              {new Date(category.season_started_at).toLocaleDateString()}
              {category.season_ends_at
                ? ` · ends ${new Date(category.season_ends_at).toLocaleDateString()}`
                : ""}
            </p>
            <p className="text-xs text-muted-foreground">
              Resetting clears every active boost on this board, restarts the countdown and puts
              entry back to the opening price. Charts also refresh automatically on the 1st of each
              month.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (!confirm(`Reset ${category.name}? All active boosts will be cleared.`)) return;
              resetMut.mutate({
                categoryId: category.id,
                all: false,
                durationDays: category.boost_duration_days,
              });
            }}
          >
            Reset this board
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (!confirm("Reset EVERY board and start a new season everywhere?")) return;
              resetMut.mutate({ all: true, durationDays: 30 });
            }}
          >
            Reset all boards
          </Button>
        </div>
      )}

      <section>
        <h2 className="font-display text-lg font-bold">Positions</h2>
        <div className="mt-3 space-y-2">
          {rows.length === 0 && (
            <p className="text-sm text-muted-foreground">No active boosts in this board.</p>
          )}
          {rows.map((b, i) => (
            <div key={b.id} className="card-surface flex flex-wrap items-center gap-3 p-3">
              <span className="rank-numeral w-8 text-lg text-primary">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{b.listing?.name ?? b.listing_id}</p>
                <p className="text-xs text-muted-foreground">
                  {formatHeat(Number(b.value_cents))} Heat · expires{" "}
                  {new Date(b.expires_at).toLocaleDateString()}
                </p>
              </div>
              <Input
                type="number"
                className="w-28"
                defaultValue={Math.round(Number(b.value_cents) / HEAT_AED_CENTS)}
                onBlur={(e) => {
                  const heat = Number(e.target.value);
                  if (heat !== Math.round(Number(b.value_cents) / HEAT_AED_CENTS))
                    boostMut.mutate({ boostId: b.id, heat });
                }}
              />
              <Button size="sm" variant="outline" onClick={() => boostMut.mutate({ boostId: b.id, extendDays: 30 })}>
                +30d
              </Button>
              <Button size="sm" variant="ghost" onClick={() => boostMut.mutate({ boostId: b.id, status: "expired" })}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      </section>

      <section className="card-surface p-4">
        <h2 className="font-display text-lg font-bold">Grant heat to a listing</h2>
        <GrantForm
          listings={allListings.map((l) => ({ id: l.id, name: l.name }))}
          onGrant={(listingId, heat, days) =>
            grantMut.mutate({ listingId, categoryId: activeCategory, heat, days })
          }
        />
      </section>
    </div>
  );
}

function GrantForm({
  listings,
  onGrant,
}: {
  listings: { id: string; name: string }[];
  onGrant: (listingId: string, heat: number, days: number) => void;
}) {
  const [listingId, setListingId] = useState("");
  const [heat, setHeat] = useState("10");
  const [days, setDays] = useState("30");

  return (
    <div className="mt-3 grid gap-3 md:grid-cols-4">
      <div className="space-y-1 md:col-span-2">
        <Label>Listing</Label>
        <select
          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          value={listingId}
          onChange={(e) => setListingId(e.target.value)}
        >
          <option value="">Select a listing…</option>
          {listings.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <Label>Heat</Label>
        <Input type="number" value={heat} onChange={(e) => setHeat(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label>Days</Label>
        <Input type="number" value={days} onChange={(e) => setDays(e.target.value)} />
      </div>
      <Button
        variant="hot"
        className="md:col-span-4"
        disabled={!listingId || !Number(heat)}
        onClick={() => onGrant(listingId, Number(heat), Number(days) || 30)}
      >
        Grant heat
      </Button>
    </div>
  );
}
